// Data-access hooks for the Sales & Invoicing screen-group (plan.md G23). Built on
// the two shared primitives from src/hooks/useApi.ts, per that file's own guidance.
// Talks to four modules' endpoints:
//   - services/api/src/modules/sales/{customers,customer-prices,sales-orders,
//     dispatches}.ts — this goal's primary domain.
//   - services/api/src/modules/invoicing/{raise,invoices,credit-notes}.ts — the
//     invoicing half of §8.4.
//   - services/api/src/modules/intake/tickets.ts — OUT-direction weighbridge
//     tickets, shared with sales dispatch per that file's own header comment.
//   - services/api/src/modules/config/* and modules/reporting/{stock,genealogy}.ts
//     — read-only lookups for the guided-flow selects + previews.
//
// NOTE on lookup-hook placement (mirrors features/stores-transfers/api.ts's own
// header note): sites/locations/products lookups are generic enough that several
// web goals want the same shapes, but this goal's S0 ownership is scoped to
// sales-invoicing.tsx (+sub-routes) and features/sales-invoicing/ only — NOT
// src/hooks/ or another feature folder. Duplicating a few small hooks here keeps
// this goal's diff inside its own registry.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreditNoteApproveResponse,
  CreditNoteCreateInput,
  CreditNoteLineInput,
  CreditNoteResponse,
  CustomerCreateInput,
  CustomerPriceCreateInput,
  CustomerPriceResponse,
  CustomerPriceUpdateInput,
  CustomerResponse,
  CustomerUpdateInput,
  DispatchLineInput,
  DispatchPostResponse,
  DispatchResponse,
  DispatchReverseResponse,
  DispatchUpdate,
  DispatchWrite,
  InvoiceApproveResponse,
  InvoiceResponse,
  InvoiceVoidResponse,
  InvoiceStatus,
  CreditNoteStatus,
  DispatchStatus,
  InvoicingMode,
  RaiseInvoicesResponse,
  SalesOrderCreateInput,
  SalesOrderLineInput,
  SalesOrderResponse,
  SalesOrderStatus,
  SalesOrderUpdateInput,
  TicketCreateInput,
  TicketResponse,
  UninvoicedResponse,
  VatTreatment,
} from '@zebra/core';
import { apiFetch } from '../../api/client';
import { useApiMutation, useApiQuery } from '../../hooks/useApi';
import type { GenealogyResponse, ListResponse, LocationRow, ProductRow, SiteRow, StockOnHandRow } from './types';

/** One-page lookup size, = the server's max `limit` (`listQueryBaseSchema`'s
 * `.max(200)`). Exported so screens that fetch one lookup page and filter
 * client-side (e.g. the sales-order detail's linked-dispatches panel) can
 * truthfully name the window size in their truncation disclosure. */
export const LOOKUP_LIMIT = 200;

// ---------------------------------------------------------------------------
// Wire-safe write types — `@zebra/core`'s money-cents fields are typed `bigint`
// on the zod-INFERRED input types (moneyCentsSchema accepts a bigint OR a
// numeric string but its `.transform` normalizes to bigint, and `z.infer`
// reflects that OUTPUT type). A browser can never actually SEND a raw bigint as
// a request body (`JSON.stringify` throws on one), so every write hook that
// touches a money-cents field uses a local "wire" variant with those fields
// typed as the numeric-string form instead — same precedent as features/
// purchasing-intake/api.ts's `SupplierContractWriteInput`.
// ---------------------------------------------------------------------------

export type DispatchLineWriteInput = Omit<DispatchLineInput, 'priceCentsPerUnit'> & { priceCentsPerUnit: string };
export type DispatchWriteWire = Omit<DispatchWrite, 'lines'> & { lines: DispatchLineWriteInput[] };
export type DispatchUpdateWire = Omit<DispatchUpdate, 'lines'> & { lines: DispatchLineWriteInput[] };

export type CustomerPriceWriteInput = Omit<CustomerPriceCreateInput, 'priceCentsPerTonne' | 'priceCentsPerUnit'> & {
  priceCentsPerTonne?: string | null;
  priceCentsPerUnit?: string | null;
};
export type CustomerPriceUpdateWriteInput = Omit<CustomerPriceUpdateInput, 'priceCentsPerTonne' | 'priceCentsPerUnit'> & {
  priceCentsPerTonne?: string | null;
  priceCentsPerUnit?: string | null;
};

export type CreditNoteLineWriteInput = Omit<CreditNoteLineInput, 'unitPriceCents'> & { unitPriceCents: string };
export type CreditNoteCreateWireInput = Omit<CreditNoteCreateInput, 'lines'> & { lines: CreditNoteLineWriteInput[] };

export type SalesOrderLineWriteInput = Omit<SalesOrderLineInput, 'priceCents'> & { priceCents: string };
export type SalesOrderCreateWireInput = Omit<SalesOrderCreateInput, 'lines'> & { lines: SalesOrderLineWriteInput[] };
export type SalesOrderUpdateWireInput = Omit<SalesOrderUpdateInput, 'lines'> & { lines?: SalesOrderLineWriteInput[] };

// ---------------------------------------------------------------------------
// Config lookups (config module, G13) — read-only pickers.
// ---------------------------------------------------------------------------

export function useSites() {
  return useApiQuery<ListResponse<SiteRow>>(['config', 'sites'], '/config/sites', { query: { limit: LOOKUP_LIMIT } });
}

export function useSite(id: string | undefined) {
  return useApiQuery<SiteRow>(['config', 'sites', 'byId', id ?? null], `/config/sites/${id}`, {
    enabled: Boolean(id),
    retry: false,
  });
}

export function useLocations(siteId: string | undefined) {
  return useApiQuery<ListResponse<LocationRow>>(['config', 'locations', siteId ?? null], '/config/locations', {
    query: { siteId, isActive: true, limit: LOOKUP_LIMIT },
    enabled: Boolean(siteId),
  });
}

/** Every active location tenant-wide, unfiltered by site — used by the credit-note
 * restock location picker (a rare, low-volume action; not worth a two-step site ->
 * location flow). Mirrors stores-transfers/api.ts's `useAllLocations`. */
export function useAllLocations() {
  return useApiQuery<ListResponse<LocationRow>>(['config', 'locations', 'all'], '/config/locations', {
    query: { isActive: true, limit: LOOKUP_LIMIT },
  });
}

export function useLocation(id: string | undefined) {
  return useApiQuery<LocationRow>(['config', 'locations', 'byId', id ?? null], `/config/locations/${id}`, {
    enabled: Boolean(id),
    retry: false,
  });
}

/** Typeahead product search (fuzzy-ish `q` substring match per config's
 * `listQueryBaseSchema`). */
export function useProductSearch(search: string) {
  return useApiQuery<ListResponse<ProductRow>>(['config', 'products', search], '/config/products', {
    query: { q: search.trim() || undefined, isActive: true, limit: 50 },
  });
}

export function useProduct(id: string | undefined) {
  return useApiQuery<ProductRow>(['config', 'products', 'byId', id ?? null], `/config/products/${id}`, {
    enabled: Boolean(id),
    retry: false,
  });
}

/** Every product (active + inactive), for O(1) baseUom/name lookups by id when
 * rendering a sales order's already-persisted lines (unit-aware qty labels —
 * see format.ts's `qtyInputLabel`/`formatLineQty`). Mirrors production-packconv/
 * api.ts's `useActiveProducts`/`useAllProducts` split, documented on its
 * `RunInputsEditor` call site: the CREATE-line picker (`ProductSelect`) already
 * scopes to active-only via `useProductSearch`'s `isActive: true`; this
 * unfiltered bulk fetch is only for resolving an existing line's productId to
 * its baseUom without a second round-trip per row, and must still resolve a
 * line whose product has since been deactivated. */
export function useAllProducts() {
  return useApiQuery<ListResponse<ProductRow>>(['config', 'products', 'all'], '/config/products', {
    query: { limit: LOOKUP_LIMIT },
  });
}

/** Every `stock_levels` row for a site, joined to its pool's derived WAC average —
 * fetched once per site, then looked up client-side per product for the dispatch
 * margin-preview ESTIMATE (mirrors stores-transfers/api.ts's `useStockOnHand`). */
export function useStockOnHand(siteId: string | undefined) {
  return useApiQuery<{ items: StockOnHandRow[]; totalValueCents: string }>(
    ['reporting', 'stock-on-hand', siteId ?? null],
    '/reporting/stock-on-hand',
    { query: { siteId }, enabled: Boolean(siteId), retry: false },
  );
}

export function avgCostByProduct(rows: StockOnHandRow[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of rows ?? []) {
    if (!map.has(row.productId)) map.set(row.productId, row.avgCostCentsPerUnit);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Genealogy (reporting module, G19) — §9.7 panel on dispatch detail.
// ---------------------------------------------------------------------------

export function useGenealogy(dispatchId: string | undefined) {
  return useApiQuery<GenealogyResponse>(['reporting', 'genealogy', dispatchId ?? null], '/reporting/genealogy', {
    query: { dispatchId },
    enabled: Boolean(dispatchId),
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Customers (sales module, G10)
// ---------------------------------------------------------------------------

const customersListKey = ['sales', 'customers', 'list'] as const;

export function useCustomers(params: { isActive?: boolean; invoicingMode?: InvoicingMode } = {}) {
  return useApiQuery<ListResponse<CustomerResponse>>([...customersListKey, params], '/sales/customers', {
    query: { isActive: params.isActive, invoicingMode: params.invoicingMode, limit: LOOKUP_LIMIT },
  });
}

export function useCustomer(id: string | undefined) {
  return useApiQuery<CustomerResponse>(['sales', 'customers', id ?? null], `/sales/customers/${id}`, {
    enabled: Boolean(id),
    retry: false,
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useApiMutation<CustomerResponse, CustomerCreateInput>('/sales/customers', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: customersListKey }),
  });
}

export function useUpdateCustomer(id: string) {
  const qc = useQueryClient();
  return useApiMutation<CustomerResponse, CustomerUpdateInput>(`/sales/customers/${id}`, 'PATCH', {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customersListKey });
      qc.invalidateQueries({ queryKey: ['sales', 'customers', id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Customer prices (sales module, G10)
// ---------------------------------------------------------------------------

const customerPricesListKey = ['sales', 'customer-prices', 'list'] as const;

export function useCustomerPrices(params: { customerId?: string; productId?: string } = {}) {
  return useApiQuery<ListResponse<CustomerPriceResponse>>(
    [...customerPricesListKey, params],
    '/sales/customer-prices',
    { query: { customerId: params.customerId, productId: params.productId, limit: LOOKUP_LIMIT } },
  );
}

/** Picks the customer price row that is valid as-of `asOf` (default now): the
 * latest `validFrom` among rows whose window [`validFrom`, `validTo`] covers it.
 * Not authoritative for posting (the server takes whatever the wizard submits) —
 * this only drives the wizard's editable price PREFILL. */
export function pickCurrentCustomerPrice(
  prices: CustomerPriceResponse[] | undefined,
  asOf: Date = new Date(),
): CustomerPriceResponse | undefined {
  const valid = (prices ?? []).filter((p) => {
    const from = new Date(p.validFrom).getTime();
    const to = p.validTo ? new Date(p.validTo).getTime() : null;
    return from <= asOf.getTime() && (to === null || to >= asOf.getTime());
  });
  if (valid.length === 0) return undefined;
  return valid.reduce((latest, p) => (new Date(p.validFrom) > new Date(latest.validFrom) ? p : latest));
}

export function useCreateCustomerPrice() {
  const qc = useQueryClient();
  return useApiMutation<CustomerPriceResponse, CustomerPriceWriteInput>('/sales/customer-prices', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: customerPricesListKey }),
  });
}

export function useUpdateCustomerPrice(id: string) {
  const qc = useQueryClient();
  return useApiMutation<CustomerPriceResponse, CustomerPriceUpdateWriteInput>(`/sales/customer-prices/${id}`, 'PATCH', {
    onSuccess: () => qc.invalidateQueries({ queryKey: customerPricesListKey }),
  });
}

// ---------------------------------------------------------------------------
// Sales orders (sales module, G10). Full CRUD + lifecycle UI (audit's #1
// functional blocker: services/api/src/modules/sales/sales-orders.ts had a
// complete backend and zero screens — the dispatch wizard's "+ sales order"
// picker below, `useOpenSalesOrders`, was the only web consumer). All queries
// share the `salesOrdersRootKey` prefix below so a single broad invalidation
// on every write refreshes the register, the detail page, AND this picker.
// ---------------------------------------------------------------------------

const salesOrdersRootKey = ['sales', 'sales-orders'] as const;

/** One RAW `sales_orders` row, exactly as three endpoints return it —
 * services/api/src/modules/sales/sales-orders.ts's GET '/' (list) is
 * `buildPage` over bare Drizzle rows, and the three lifecycle postings
 * (/open, /complete, /cancel) return `transitionSalesOrder`'s bare updated
 * row. Only the by-id GET and create/PATCH go through `serializeSalesOrder`,
 * which is what attaches `lines` — so `@zebra/core`'s `SalesOrderResponse`
 * (with `lines`) is WRONG for these endpoints (2026-07-10 wave-gate BLOCKER:
 * the register rendered `so.lines.length` off this lie and crashed on any
 * real row). A caller that needs lines must fetch the detail. Timestamps are
 * the wire ISO strings `c.json` produces from the row's Date columns. */
export interface SalesOrderRow {
  id: string;
  tenantId: string;
  siteId: string;
  number: string;
  customerId: string;
  status: SalesOrderStatus;
  reference: string | null;
  notes: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export function useSalesOrdersList(params: { cursor?: string; status?: SalesOrderStatus; customerId?: string } = {}) {
  return useApiQuery<{ items: SalesOrderRow[]; nextCursor: string | null }>(
    [...salesOrdersRootKey, 'list', params],
    '/sales/sales-orders',
    { query: { cursor: params.cursor, status: params.status, customerId: params.customerId, limit: 20 } },
  );
}

export function useSalesOrder(id: string | undefined) {
  return useApiQuery<SalesOrderResponse>([...salesOrdersRootKey, 'byId', id ?? null], `/sales/sales-orders/${id}`, {
    enabled: Boolean(id),
  });
}

export function useCreateSalesOrder() {
  const qc = useQueryClient();
  return useApiMutation<SalesOrderResponse, SalesOrderCreateWireInput>('/sales/sales-orders', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: salesOrdersRootKey }),
  });
}

export function useUpdateSalesOrder(id: string) {
  const qc = useQueryClient();
  return useApiMutation<SalesOrderResponse, SalesOrderUpdateWireInput>(`/sales/sales-orders/${id}`, 'PATCH', {
    onSuccess: () => qc.invalidateQueries({ queryKey: salesOrdersRootKey }),
  });
}

// The three lifecycle postings return the bare updated row (no lines) — see
// SalesOrderRow's own doc comment. Callers only await + rely on invalidation.

export function useOpenSalesOrder(id: string) {
  const qc = useQueryClient();
  return useApiMutation<SalesOrderRow, void>(`/sales/sales-orders/${id}/open`, 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: salesOrdersRootKey }),
  });
}

export function useCompleteSalesOrder(id: string) {
  const qc = useQueryClient();
  return useApiMutation<SalesOrderRow, void>(`/sales/sales-orders/${id}/complete`, 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: salesOrdersRootKey }),
  });
}

export function useCancelSalesOrder(id: string) {
  const qc = useQueryClient();
  return useApiMutation<SalesOrderRow, void>(`/sales/sales-orders/${id}/cancel`, 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: salesOrdersRootKey }),
  });
}

/** Scoped to a customer's OPEN sales orders only — the dispatch wizard's
 * optional "+ sales order" picker (BUILD_SPEC.md §8.4 step 1: "pick customer
 * (+ optional sales order)"). Shares `salesOrdersRootKey`'s prefix so opening/
 * completing/cancelling an order (above) invalidates this picker's cache too —
 * e.g. an order just Opened must appear here without a manual refresh. */
export function useOpenSalesOrders(customerId: string | undefined) {
  return useApiQuery<ListResponse<SalesOrderRow>>(
    [...salesOrdersRootKey, 'open', customerId ?? null],
    '/sales/sales-orders',
    { query: { customerId, status: 'OPEN', limit: LOOKUP_LIMIT }, enabled: Boolean(customerId) },
  );
}

// ---------------------------------------------------------------------------
// Weighbridge tickets (intake module, G14) — OUT direction only in this feature.
// ---------------------------------------------------------------------------

export function useCreateTicket() {
  return useApiMutation<TicketResponse, TicketCreateInput>('/intake/weighbridge-tickets', 'POST');
}

// ---------------------------------------------------------------------------
// Dispatches (sales module, G10)
// ---------------------------------------------------------------------------

const dispatchesListKey = ['sales', 'dispatches', 'list'] as const;

export function useDispatchesList(params: { cursor?: string; status?: DispatchStatus; customerId?: string } = {}) {
  return useApiQuery<{ items: DispatchListRow[]; nextCursor: string | null }>(
    [...dispatchesListKey, params],
    '/sales/dispatches',
    { query: { cursor: params.cursor, status: params.status, customerId: params.customerId, limit: 20 } },
  );
}

export function useDispatch(id: string | undefined) {
  return useApiQuery<DispatchResponse>(['sales', 'dispatches', id ?? null], `/sales/dispatches/${id}`, {
    enabled: Boolean(id),
  });
}

/** One `GET /sales/dispatches` (list) item — like the sales-orders list (see
 * `SalesOrderRow`), that endpoint returns raw `dispatches` rows with NO
 * `lines` (only the by-id GET runs `serializeDispatch`; 2026-07-10 wave-gate
 * BLOCKER: the register rendered `dispatch.lines.length` off the wrong
 * `DispatchResponse` type and crashed on any real row), PLUS the two line
 * aggregates the endpoint's grouped subquery attaches per row
 * (services/api/src/modules/sales/dispatches.ts's GET '/'):
 *   - `lineCount` — number of lines on the dispatch;
 *   - `totalMarginCents` — summed line margins as a decimal string, or null
 *     while ANY line's margin is unpriced (DRAFT lines are only priced at
 *     post) — null-propagating, never a misleading partial total.
 * Fields limited to what the register + the sales-order detail's
 * linked-dispatches panel read. A caller that needs line DETAIL must fetch
 * the by-id shape. */
export interface DispatchListRow {
  id: string;
  number: string;
  customerId: string;
  salesOrderId: string | null;
  status: DispatchStatus;
  dispatchedAt: string;
  lineCount: number;
  totalMarginCents: string | null;
}

/** Every dispatch for a sales order's customer, for the sales-order detail
 * page's "dispatches against this order" list. `GET /sales/dispatches` has NO
 * `salesOrderId` filter — `dispatchListQuerySchema` above only accepts
 * `status`/`customerId` (verified: not an oversight to fix here, this goal is
 * UI-only) — so this fetches one generous customer-scoped page and filters to
 * the order's id client-side. Same precedent as `useCreditableInvoices` above
 * (that hook's own comment: the list endpoint's filter is narrower than what
 * the screen needs, so fetch broad + filter in the component).
 *
 * TRUNCATION CAVEAT (wave-gate finding, 2026-07-10): one page = at most
 * `LOOKUP_LIMIT` (200) rows, and the endpoint orders by id ASCENDING (ULIDs =
 * chronological), so a busy customer's page is their OLDEST 200 dispatches —
 * anything newer is silently absent. Callers MUST check `nextCursor` and
 * disclose the truncation when it is non-null (the sales-order detail page
 * renders a disclosure line pointing at the Dispatches register). */
export function useDispatchesForCustomer(customerId: string | undefined) {
  return useApiQuery<{ items: DispatchListRow[]; nextCursor: string | null }>(
    [...dispatchesListKey, 'byCustomer', customerId ?? null],
    '/sales/dispatches',
    { query: { customerId, limit: LOOKUP_LIMIT }, enabled: Boolean(customerId) },
  );
}

export function useCreateDispatch() {
  const qc = useQueryClient();
  return useApiMutation<DispatchResponse, DispatchWriteWire>('/sales/dispatches', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: dispatchesListKey }),
  });
}

export function useUpdateDispatch(id: string) {
  const qc = useQueryClient();
  return useApiMutation<DispatchResponse, DispatchUpdateWire>(`/sales/dispatches/${id}`, 'PATCH', {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dispatchesListKey });
      qc.invalidateQueries({ queryKey: ['sales', 'dispatches', id] });
    },
  });
}

export function usePostDispatch(id: string) {
  const qc = useQueryClient();
  return useApiMutation<DispatchPostResponse, void>(`/sales/dispatches/${id}/post`, 'POST', {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dispatchesListKey });
      qc.invalidateQueries({ queryKey: ['sales', 'dispatches', id] });
      qc.invalidateQueries({ queryKey: ['reporting', 'stock-on-hand'] });
      qc.invalidateQueries({ queryKey: ['invoicing'] });
    },
  });
}

export function useReverseDispatch(id: string) {
  const qc = useQueryClient();
  return useApiMutation<DispatchReverseResponse, void>(`/sales/dispatches/${id}/reverse`, 'POST', {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dispatchesListKey });
      qc.invalidateQueries({ queryKey: ['sales', 'dispatches', id] });
      qc.invalidateQueries({ queryKey: ['reporting', 'stock-on-hand'] });
    },
  });
}

// ---------------------------------------------------------------------------
// "Dispatch a load" (§8.4 steps 1-3) — a composite action spanning three
// dedicated posting endpoints (create OUT ticket -> create dispatch -> post
// dispatch), optionally followed by a fourth (raise the PER_LOAD invoice
// immediately — the "immediate" per-load experience ratified in BUILD_SPEC.md
// §8.4 step 4's amendment). Mirrors features/purchasing-intake/api.ts's
// `useReceiveLoad` composite pattern: NOT a single `useApiMutation` (that helper
// is for one fixed path) — this is the one place in this feature that calls
// `apiFetch` directly, composing several calls into a single mutation so the
// wizard gets one isPending/error/data lifecycle for the whole flow.
// ---------------------------------------------------------------------------

export interface DispatchLoadInput {
  siteId: string;
  customerId: string;
  salesOrderId?: string | null;
  productId: string;
  fromLocationId: string;
  vehicleReg: string;
  externalTicketNo?: string | null;
  haulier?: string | null;
  driver?: string | null;
  grossKg: string;
  tareKg: string;
  /** `datetime-local` input value — also used as the dispatch's own `dispatchedAt`. */
  weighedAt: string;
  priceCentsPerUnit: string;
  vatTreatment: VatTreatment;
  notes?: string | null;
  /** When true, immediately calls `POST /invoicing/raise` for this customer right
   * after posting — the one-click "post & raise invoice" affordance for PER_LOAD
   * customers (BUILD_SPEC.md §8.4 step 4). Never set for CONSOLIDATED customers
   * (the wizard only offers this button when `customer.invoicingMode ===
   * 'PER_LOAD'`) — the API would otherwise 400 for lacking a period. */
  alsoRaiseInvoice: boolean;
}

export interface DispatchLoadResult {
  post: DispatchPostResponse;
  raise: RaiseInvoicesResponse | null;
}

export function useDispatchLoad() {
  const qc = useQueryClient();
  return useMutation<DispatchLoadResult, Error, DispatchLoadInput>({
    mutationFn: async (input) => {
      const ticketBody: TicketCreateInput = {
        siteId: input.siteId,
        direction: 'OUT',
        externalTicketNo: input.externalTicketNo ?? null,
        vehicleReg: input.vehicleReg,
        haulier: input.haulier ?? null,
        driver: input.driver ?? null,
        grossKg: input.grossKg,
        tareKg: input.tareKg,
        mcPct: null,
        mcSource: null,
        weighedAt: new Date(input.weighedAt),
        purchaseOrderId: null,
        salesOrderId: input.salesOrderId ?? null,
        customerId: input.customerId,
        productId: input.productId,
        notes: input.notes ?? null,
      };
      const ticket = await apiFetch<TicketResponse>('/intake/weighbridge-tickets', { method: 'POST', body: ticketBody });

      const dispatchBody: DispatchWriteWire = {
        siteId: input.siteId,
        customerId: input.customerId,
        salesOrderId: input.salesOrderId ?? null,
        ticketId: ticket.id,
        dispatchedAt: new Date(input.weighedAt),
        notes: input.notes ?? null,
        lines: [
          {
            lineNo: 1,
            productId: input.productId,
            fromLocationId: input.fromLocationId,
            qty: ticket.netKg,
            priceCentsPerUnit: input.priceCentsPerUnit,
            vatTreatment: input.vatTreatment,
          },
        ],
      };
      const dispatch = await apiFetch<DispatchResponse>('/sales/dispatches', { method: 'POST', body: dispatchBody });
      const post = await apiFetch<DispatchPostResponse>(`/sales/dispatches/${dispatch.id}/post`, { method: 'POST' });

      let raise: RaiseInvoicesResponse | null = null;
      if (input.alsoRaiseInvoice) {
        raise = await apiFetch<RaiseInvoicesResponse>('/invoicing/raise', {
          method: 'POST',
          body: { customerIds: [input.customerId] },
        });
      }
      return { post, raise };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dispatchesListKey });
      qc.invalidateQueries({ queryKey: ['reporting', 'stock-on-hand'] });
      qc.invalidateQueries({ queryKey: ['invoicing'] });
    },
  });
}

// ---------------------------------------------------------------------------
// "Dispatch without weighbridge" — ticket-less dispatch for counted goods
// (boxed firelighters, bagged/crated firewood) that were never going to cross
// a weighbridge. A composite action spanning TWO dedicated posting endpoints
// (create dispatch -> post dispatch), optionally a third (raise the PER_LOAD
// invoice immediately, same as `useDispatchLoad` below) — but, unlike
// `useDispatchLoad`, this NEVER creates a weighbridge ticket: fabricating an
// OUT ticket's gross/tare kg for goods that were counted, not weighed, is
// exactly the defect this screen exists to remove.
// `dispatchWriteSchema.ticketId` (packages/core/src/schemas/sales.ts) is
// nullable/optional, and the Howick e2e test (services/api/src/modules/
// config/howick-e2e.test.ts, "Step 6: dispatch all 294 bags") posts a
// dispatch the same way — `ticketId` simply omitted from the body — proving
// the backend already supports this path with no API changes required.
//
// RETRY SAFETY (wave-gate BLOCKER fix — same class as purchasing-intake/
// api.ts's `ReceiveLoadPostError`): each partial-failure point throws a TYPED
// error carrying the already-created dispatch's identity so the caller can
// resume the SAME document instead of re-running the whole composite (which
// would create-and-post a SECOND dispatch — duplicate stock relief + revenue):
//   - post fails after create  -> DispatchManualPostError{dispatchId, number}.
//     Retry: pass `resumeDispatchId` — the hook PATCHes the existing DRAFT
//     with the current form body and re-posts it; never a second create.
//   - the resume-PATCH itself fails -> DispatchManualResumeError{dispatchId}.
//     The draft may no longer be resumable at all (another operator posted or
//     reversed it between the failure and this retry -> 409 "Only DRAFT
//     dispatches can be edited") — the caller must fetch the dispatch's
//     CURRENT status and re-route: already POSTED -> show it as posted (never
//     "could not post"); dead (REVERSED/unknown) -> unpin the id so the next
//     attempt creates a fresh dispatch. Re-looping the same PATCH forever is
//     exactly the trap this typed error exists to break (delta re-gate MAJOR,
//     2026-07-10).
//   - raise fails after post   -> DispatchManualRaiseError{dispatchId, post}.
//     The dispatch DID post — the caller must NOT re-run this mutation at all;
//     it renders the posted result from `error.post` and retries the raise
//     alone (plain `useRaiseInvoices`).
// ---------------------------------------------------------------------------

export interface DispatchManualInput {
  siteId: string;
  customerId: string;
  salesOrderId?: string | null;
  /** `datetime-local` input value — also used as the dispatch's own `dispatchedAt`. */
  dispatchedAt: string;
  notes?: string | null;
  lines: DispatchLineWriteInput[];
  /** Same one-click "post & raise invoice" affordance `useDispatchLoad` offers
   * for PER_LOAD customers (BUILD_SPEC.md §8.4 step 4) — never set for
   * CONSOLIDATED customers by the caller. */
  alsoRaiseInvoice: boolean;
  /** A DRAFT dispatch id from a previous attempt's `DispatchManualPostError` —
   * when set, the hook resumes that document (PATCH + post) instead of
   * creating a new one. */
  resumeDispatchId?: string | null;
}

export interface DispatchManualResult {
  post: DispatchPostResponse;
  raise: RaiseInvoicesResponse | null;
}

/** Thrown when the dispatch was CREATED (as DRAFT) but posting it failed —
 * carries the draft's id/number so the next attempt resumes it via
 * `resumeDispatchId` rather than creating a duplicate. Mirrors
 * purchasing-intake/api.ts's `ReceiveLoadPostError`. */
export class DispatchManualPostError extends Error {
  readonly dispatchId: string;
  readonly dispatchNumber: string;
  readonly originalError: unknown;

  constructor(dispatchId: string, dispatchNumber: string, originalError: unknown) {
    super(originalError instanceof Error ? originalError.message : 'Failed to post the dispatch');
    this.name = 'DispatchManualPostError';
    this.dispatchId = dispatchId;
    this.dispatchNumber = dispatchNumber;
    this.originalError = originalError;
  }
}

/** Thrown when a RESUME attempt's PATCH on the previously-created draft
 * failed — distinct from `DispatchManualPostError` because the contract is
 * the opposite: that error says "resume me with this id"; this one says
 * "STOP resuming this id until you've checked what it is now". The usual
 * cause is the draft having been posted or reversed by someone else in the
 * meantime (the DRAFT-only PATCH then 409s), so the caller fetches the
 * dispatch's current status and either presents it as already-posted or
 * unpins the id so the next attempt creates a fresh dispatch. */
export class DispatchManualResumeError extends Error {
  readonly dispatchId: string;
  readonly originalError: unknown;

  constructor(dispatchId: string, originalError: unknown) {
    super(originalError instanceof Error ? originalError.message : 'Failed to update the draft dispatch');
    this.name = 'DispatchManualResumeError';
    this.dispatchId = dispatchId;
    this.originalError = originalError;
  }
}

/** Thrown when the dispatch POSTED successfully but the follow-on
 * `/invoicing/raise` call failed — the stock and revenue effects are REAL at
 * this point, so the caller must surface success-with-a-caveat (never "could
 * not post") and retry only the raise. Carries the full post response so the
 * caller can render the posted view without refetching. */
export class DispatchManualRaiseError extends Error {
  readonly dispatchId: string;
  readonly post: DispatchPostResponse;
  readonly originalError: unknown;

  constructor(post: DispatchPostResponse, originalError: unknown) {
    super(originalError instanceof Error ? originalError.message : 'Failed to raise the invoice');
    this.name = 'DispatchManualRaiseError';
    this.dispatchId = post.dispatch.id;
    this.post = post;
    this.originalError = originalError;
  }
}

export function useDispatchManual() {
  const qc = useQueryClient();
  return useMutation<DispatchManualResult, Error, DispatchManualInput>({
    mutationFn: async (input) => {
      const dispatchBody: DispatchWriteWire = {
        siteId: input.siteId,
        customerId: input.customerId,
        salesOrderId: input.salesOrderId ?? null,
        ticketId: null,
        dispatchedAt: new Date(input.dispatchedAt),
        notes: input.notes ?? null,
        lines: input.lines,
      };
      // Resume path: PATCH the existing DRAFT with the CURRENT form body (the
      // user may have fixed the very thing that made the post fail), then
      // re-post it — the same PATCH-then-post recovery the receive side runs
      // via GoodsReceiptDraftModal. Fresh path: one create. The PATCH is
      // wrapped in its own typed error (delta re-gate MAJOR fix): if the draft
      // stopped being editable between failure and retry (posted/reversed by
      // another operator -> 409), the caller must re-route, not re-loop.
      let dispatch: DispatchResponse;
      if (input.resumeDispatchId) {
        try {
          dispatch = await apiFetch<DispatchResponse>(`/sales/dispatches/${input.resumeDispatchId}`, { method: 'PATCH', body: dispatchBody });
        } catch (err) {
          throw new DispatchManualResumeError(input.resumeDispatchId, err);
        }
      } else {
        dispatch = await apiFetch<DispatchResponse>('/sales/dispatches', { method: 'POST', body: dispatchBody });
      }

      let post: DispatchPostResponse;
      try {
        post = await apiFetch<DispatchPostResponse>(`/sales/dispatches/${dispatch.id}/post`, { method: 'POST' });
      } catch (err) {
        throw new DispatchManualPostError(dispatch.id, dispatch.number, err);
      }

      let raise: RaiseInvoicesResponse | null = null;
      if (input.alsoRaiseInvoice) {
        try {
          raise = await apiFetch<RaiseInvoicesResponse>('/invoicing/raise', {
            method: 'POST',
            body: { customerIds: [input.customerId] },
          });
        } catch (err) {
          throw new DispatchManualRaiseError(post, err);
        }
      }
      return { post, raise };
    },
    // onSettled, not onSuccess: a DispatchManualPostError still created a DRAFT
    // (dispatches register is stale) and a DispatchManualRaiseError means the
    // post's stock/margin effects are already real — the caches must refresh on
    // every outcome, not just the all-steps-succeeded one.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dispatchesListKey });
      qc.invalidateQueries({ queryKey: ['reporting', 'stock-on-hand'] });
      qc.invalidateQueries({ queryKey: ['invoicing'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Raise invoices (invoicing module, G15) — §8.4 step 4.
// ---------------------------------------------------------------------------

export function useUninvoiced() {
  return useApiQuery<UninvoicedResponse>(['invoicing', 'uninvoiced'], '/invoicing/uninvoiced');
}

export function useRaiseInvoices() {
  const qc = useQueryClient();
  return useApiMutation<RaiseInvoicesResponse, { customerIds: string[]; periodFrom?: Date; periodTo?: Date }>(
    '/invoicing/raise',
    'POST',
    {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['invoicing'] });
        qc.invalidateQueries({ queryKey: dispatchesListKey });
      },
    },
  );
}

// ---------------------------------------------------------------------------
// Invoices (invoicing module, G15) — §8.4 step 5.
// ---------------------------------------------------------------------------

const invoicesListKey = ['invoicing', 'invoices', 'list'] as const;

export function useInvoicesList(params: { cursor?: string; status?: InvoiceStatus; customerId?: string } = {}) {
  return useApiQuery<{ items: InvoiceResponse[]; nextCursor: string | null }>(
    [...invoicesListKey, params],
    '/invoicing/invoices',
    { query: { cursor: params.cursor, status: params.status, customerId: params.customerId, limit: 20 } },
  );
}

/** Every creditable (APPROVED/SYNCED/PAID) invoice, for the credit-note "reference
 * an invoice" picker — fetched as one generous unfiltered page and filtered
 * client-side (the list endpoint's `status` filter only accepts ONE status;
 * see `packages/core/src/schemas/invoicing.ts`'s `invoiceListQuerySchema`). */
export function useCreditableInvoices() {
  return useApiQuery<{ items: InvoiceResponse[]; nextCursor: string | null }>(
    [...invoicesListKey, 'creditable'],
    '/invoicing/invoices',
    { query: { limit: LOOKUP_LIMIT } },
  );
}

export function useInvoice(id: string | undefined) {
  return useApiQuery<InvoiceResponse>(['invoicing', 'invoices', id ?? null], `/invoicing/invoices/${id}`, {
    enabled: Boolean(id),
  });
}

export function useApproveInvoice(id: string) {
  const qc = useQueryClient();
  return useApiMutation<InvoiceApproveResponse, void>(`/invoicing/invoices/${id}/approve`, 'POST', {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: invoicesListKey });
      qc.invalidateQueries({ queryKey: ['invoicing', 'invoices', id] });
    },
  });
}

export function useVoidInvoice(id: string) {
  const qc = useQueryClient();
  return useApiMutation<InvoiceVoidResponse, void>(`/invoicing/invoices/${id}/void`, 'POST', {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: invoicesListKey });
      qc.invalidateQueries({ queryKey: ['invoicing', 'invoices', id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Credit notes (invoicing module, G15) — §8.4 step 5.
// ---------------------------------------------------------------------------

const creditNotesListKey = ['invoicing', 'credit-notes', 'list'] as const;

export function useCreditNotesList(params: { cursor?: string; status?: CreditNoteStatus; invoiceId?: string } = {}) {
  return useApiQuery<{ items: CreditNoteResponse[]; nextCursor: string | null }>(
    [...creditNotesListKey, params],
    '/invoicing/credit-notes',
    { query: { cursor: params.cursor, status: params.status, invoiceId: params.invoiceId, limit: 20 } },
  );
}

export function useCreditNote(id: string | undefined) {
  return useApiQuery<CreditNoteResponse>(['invoicing', 'credit-notes', id ?? null], `/invoicing/credit-notes/${id}`, {
    enabled: Boolean(id),
  });
}

export function useCreateCreditNote() {
  const qc = useQueryClient();
  return useApiMutation<CreditNoteResponse, CreditNoteCreateWireInput>('/invoicing/credit-notes', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: creditNotesListKey }),
  });
}

export function useApproveCreditNote(id: string) {
  const qc = useQueryClient();
  return useApiMutation<CreditNoteApproveResponse, void>(`/invoicing/credit-notes/${id}/approve`, 'POST', {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: creditNotesListKey });
      qc.invalidateQueries({ queryKey: ['invoicing', 'credit-notes', id] });
      qc.invalidateQueries({ queryKey: ['reporting', 'stock-on-hand'] });
    },
  });
}

export function useVoidCreditNote(id: string) {
  const qc = useQueryClient();
  return useApiMutation<{ creditNote: CreditNoteResponse }, void>(`/invoicing/credit-notes/${id}/void`, 'POST', {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: creditNotesListKey });
      qc.invalidateQueries({ queryKey: ['invoicing', 'credit-notes', id] });
    },
  });
}
